import type { FieldsSchema } from "@repo/adaptive-form/schema";
import type {
  SalesPartner,
  UiTextDictionaryId,
} from "src/gql/__generated__/types";
import { graphql, type ResultOf } from "src/gql/graphql";
import type { ChatConfigSchema } from "src/jsonSchema/chatConfigSchema";
import type { DocumentConfigSchema } from "src/jsonSchema/documentConfigSchema";
import type { MessagingConfigSchema } from "src/jsonSchema/messagingConfigSchema";
import type { KarteSchema } from "../../jsonSchema/karteSchema";

const AiAppFragment = graphql(`
  fragment AiApp on AiApp @_unmask {
    id
    enabled
    appId
    activeConfig {
      id
      config
    }
  }
`);

const LineConnectionSettingMessagingApiFragment = graphql(`
  fragment LineConnectionSettingMessagingApi on LineConnectionSettingMessagingApi @_unmask {
    id
    channelId
    channelSecret
    botInfo {
      basicId
      premiumId
    }
  }
`);

const LineConnectionSettingLineLoginFragment = graphql(`
  fragment LineConnectionSettingLineLogin on LineConnectionSettingLineLogin @_unmask {
    id
    channelId
    channelSecret
  }
`);

const ShopStaffFragment = graphql(`
  fragment ShopStaff on ShopStaff @_unmask {
    id
    firstName
    lastName
    deactivatedAt
  }
`);

const EsConnectionSettingFragment = graphql(`
  fragment EsConnectionSetting on EsConnectionSetting @_unmask {
    id
    fieldMappings {
      esFieldKey
      karteFieldKey
    }
  }
`);

const EsSyncConnectionFragment = graphql(`
  fragment EsSyncConnection on EsSyncConnection @_unmask {
    id
    enabled
    syncDaysAhead
    sameDaySyncEnabled
    credential {
      companyCd
      sn3
      hasAccessKey
    }
    staffAssignmentRules {
      meetingType
      charge
    }
  }
`);

const LineConnectionSettingFragment = graphql(
  `
  fragment LineConnectionSetting on LineConnectionSetting @_unmask {
    id
    completed
    messagingApi {
      ...LineConnectionSettingMessagingApi
    }
    lineLogin {
      ...LineConnectionSettingLineLogin
    }
  }
`,
  [
    LineConnectionSettingMessagingApiFragment,
    LineConnectionSettingLineLoginFragment,
  ],
);

const QuestionnaireFragment = graphql(`
  fragment Questionnaire on Questionnaire @_unmask {
    id
    title
  }
`);

export const WorkspaceGlobalsFragment = graphql(
  `
  fragment WorkspaceGlobals on Workspace @_unmask {
    salesPartner
    activeCustomerFieldsSchema {
      id
      schema
    }
    activeCustomerVisitFieldsSchema {
      id
      schema
    }
    activeKarteSchemaSeries {
      id
      name
      activeKarteSchema {
        id
        schema
      }
      primary
    }
    config {
      id
      identifierType
      identifierNullable
      nameFormat
      honorificType
      uiTextDictionaryId
      agentConfig {
        shopPropertiesPrompt
      }
      bulletinEnabled
      realtimeKartePreviewEnabled
      messagingTaskEnabled
      todoEnabled
      staffVoiceMatchEnabled
      partnerSharingEnabled
      kintoneConnectionEnabled
    }
    shopStaffs {
      ...ShopStaff
    }
    lineConnectionSetting {
      ...LineConnectionSetting
    }
    esConnectionSetting {
      ...EsConnectionSetting
    }
    kintoneSyncConnection { id nameFieldCode credential { domain appId guestSpaceId } }
    esSyncConnection {
      ...EsSyncConnection
    }
    questionnaires {
      ...Questionnaire
    }
    aiApps {
      ...AiApp
    }
  }
`,
  [
    ShopStaffFragment,
    LineConnectionSettingFragment,
    EsConnectionSettingFragment,
    EsSyncConnectionFragment,
    QuestionnaireFragment,
    AiAppFragment,
  ],
);

export type ShopStaff = ResultOf<typeof ShopStaffFragment>;
export type LineConnectionSetting = ResultOf<
  typeof LineConnectionSettingFragment
>;
export type LineConnectionSettingMessagingApi = ResultOf<
  typeof LineConnectionSettingMessagingApiFragment
>;
export type LineConnectionSettingLineLogin = ResultOf<
  typeof LineConnectionSettingLineLoginFragment
>;
export type Questionnaire = ResultOf<typeof QuestionnaireFragment>;

export type ValidatedMessagingApp = {
  id: string;
  enabled: boolean;
  appId: "messaging";
  activeConfig: {
    id: string;
    config: MessagingConfigSchema;
  };
};

export type ValidatedChatApp = {
  id: string;
  enabled: boolean;
  appId: "chat";
  activeConfig: {
    id: string;
    config: ChatConfigSchema;
  };
};

export type ValidatedDocumentApp = {
  id: string;
  enabled: boolean;
  appId: "document";
  activeConfig: {
    id: string;
    config: DocumentConfigSchema;
  };
};

export type ValidatedAiApp =
  | ValidatedMessagingApp
  | ValidatedChatApp
  | ValidatedDocumentApp;

export type ValidatedKarteSchema = {
  id: string;
  schema: KarteSchema;
};

export type ValidatedKarteSchemaSeries = {
  id: string;
  name: string;
  activeKarteSchema: ValidatedKarteSchema;
  primary: boolean;
};

export type EsConnectionSetting = ResultOf<typeof EsConnectionSettingFragment>;
export type KintoneSyncConnection = NonNullable<
  ResultOf<typeof WorkspaceGlobalsFragment>["kintoneSyncConnection"]
>;
export type EsSyncConnection = ResultOf<typeof EsSyncConnectionFragment>;

export type ValidatedWorkspaceGlobals = {
  salesPartner: SalesPartner | null;
  activeCustomerFieldsSchema: {
    id: string;
    schema: FieldsSchema;
  };
  activeCustomerVisitFieldsSchema: {
    id: string;
    schema: FieldsSchema;
  };
  primaryKarteSchemaSeries: ValidatedKarteSchemaSeries;
  activeKarteSchemaSeries: ValidatedKarteSchemaSeries[];
  config: Config;
  shopStaffs: ShopStaff[];
  lineConnectionSetting: LineConnectionSetting | null;
  esConnectionSetting: EsConnectionSetting | null;
  esSyncConnection: EsSyncConnection | null;
  kintoneSyncConnection: KintoneSyncConnection | null;
  questionnaires: Questionnaire[];
  aiApps: ValidatedAiApp[];
};

export type HonorificType = "Sama" | "Goryoke";

export type Config = {
  id: string;
  identifierType: "Phone" | "General";
  identifierNullable: boolean;
  nameFormat: "Separated" | "Unified";
  honorificType: HonorificType;
  uiTextDictionaryId: UiTextDictionaryId;
  agentConfig: {
    shopPropertiesPrompt: string | null;
  };
  bulletinEnabled: boolean;
  realtimeKartePreviewEnabled: boolean;
  messagingTaskEnabled: boolean;
  todoEnabled: boolean;
  staffVoiceMatchEnabled: boolean;
  partnerSharingEnabled: boolean;
  kintoneConnectionEnabled: boolean;
};
